import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Package,
  Truck,
  Settings,
  Bell,
  Search,
  LogOut,
  GitBranch,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';

interface LayoutProps {
  children: ReactNode;
}

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Briefcase, label: 'Jobs Pipeline', path: '/jobs' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: Package, label: 'Stock', path: '/stock' },
  { icon: Truck, label: 'Delivery Notes', path: '/delivery-notes' },
  { icon: GitBranch, label: 'Workflows', path: '/settings/workflows' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Header */}
      <header className="bg-dark-surface border-b border-dark-border px-6 py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1
              onClick={() => navigate('/')}
              className="text-2xl font-bold gradient-text cursor-pointer"
            >
              SAM
            </h1>
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" size={20} />
              <input
                type="text"
                placeholder="Search jobs, customers..."
                className="input pl-10 w-80"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="relative p-2 text-dark-muted hover:text-dark-text transition-colors">
              <Bell size={24} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-status-red rounded-full" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-medium">
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </div>
              <div className="hidden md:block">
                <p className="font-medium">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-sm text-dark-muted">{user?.role_name || 'User'}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-dark-muted hover:text-status-red transition-colors"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-73px)] bg-dark-surface border-r border-dark-border p-4 sticky top-[73px] hidden md:block">
          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive =
                item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path);

              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-500/10 text-primary-500'
                      : 'text-dark-muted hover:bg-dark-elevated hover:text-dark-text'
                  }`}
                >
                  <item.icon size={20} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-[calc(100vh-73px)]">{children}</main>
      </div>
    </div>
  );
}
